import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Back navigation safe for deep-link landings.
 *
 * Problem: deep-links enter via `navigate(target, { replace: true })` which
 * does NOT push a new entry — the history stack has exactly 1 entry (idx === 0).
 * A raw `navigate(-1)` is a no-op in this state.
 *
 * Solution: read `window.history.state.idx` (set by @remix-run/router in
 * react-router-dom 6.x). When idx === 0 (or state is absent), fall back to
 * navigating home. Otherwise, normal back navigation.
 *
 * @returns A stable callback suitable for onBack props and platform.showBackButton.
 */
export function useSafeBack(): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = (window.history.state?.idx ?? 0) as number;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate]);
}
